import { z } from 'zod';
import { apiRequest } from '../masterdata/repository';

// Procurement contracts from backend origin/release 9299b95.
// NUMERIC response fields remain decimal text.
const decimal = z.union([z.string(), z.number()]).nullable();
const text = z.string().nullable();
const purchaseOrder = z.object({
  japan_manufacturer_po_number: text.optional(),
  id: z.string(),
  po_number: z.string(),
  order_date: text.optional(),
  supplier_id: z.string(),
  factory_id: z.string(),
  supply_route_id: z.string(),
  customer_id: z.string().nullable(),
  transport_mode: text.optional(),
  yen_total: decimal,
  dollar_total: decimal,
  status: z.string(),
  created_by: z.string(),
  approved_by: text,
  approved_at: text,
  created_at: z.string(),
  exported_file_reference: text,
});
const purchaseLine = z.object({
  japan_manufacturer_po_number: text.optional(),
  id: z.string(),
  line_number: z.number(),
  product_id: z.string(),
  customer_id: z.string(),
  location_id: z.string(),
  line_type: z.enum(['standard', 'price_adjustment']),
  quantity: decimal,
  whole_box_quantity: decimal.optional(),
  packaging_status: text.optional(),
  box_quantity_warning: z.boolean(),
  unit_price: decimal,
  currency_id: z.string(),
  line_total: decimal,
  incoterms_location: text.optional(),
  confirmed_quantity: decimal,
});
const purchaseDetail = purchaseOrder.extend({
  lines: z.array(purchaseLine),
  japan_purchase_order: z.object({ id: z.string(), po_number: z.string() }).nullable(),
});
const allocationMutation = z.object({
  id: z.string(),
  purchase_order_line_id: z.string(),
  order_number: z.string(),
  line_number: z.number(),
  batch_code: text,
  batch_lot_id: text,
  allocated_quantity: decimal,
  status: z.string(),
  cancellation_reason: text,
  created_by: z.string(),
  created_at: z.string(),
  changed_by: text,
  changed_at: text,
  cancelled_by: text,
  cancelled_at: text,
});
const allocation = allocationMutation.extend({
  purchase_order_id: z.string(),
  purchase_order_number: z.string(),
  purchase_order_status: z.enum(['built', 'reviewed', 'approved', 'exported', 'emailed']),
});
const forecast = z.object({
  id: z.string(),
  submission_date: z.string(),
  version: z.number(),
  exported_file_reference: text,
  created_by: z.string(),
  created_at: z.string(),
});
const forecastDetail = forecast.extend({
  product_changes: z
    .array(
      z.object({
        product_id: z.string(),
        quantity: decimal,
        previous_quantity: decimal,
        change_vs_previous: decimal,
      })
    )
    .optional(),
  lines: z.array(
    z.object({
      id: z.string(),
      product_id: z.string(),
      destination_location_id: text.optional(),
      source_snapshot: z.record(z.unknown()).nullable().optional(),
      planned_shipping_date: z.string(),
      fab_phase_id: z.string(),
      quantity: decimal,
      change_vs_previous: decimal,
    })
  ),
});
const reply = z.object({
  id: z.string(),
  product_id: z.string(),
  reply_date: z.string(),
  lot_number: z.string(),
  dispatch_date: text,
  expected_arrival_date: text,
  destination_location_id: z.string(),
  destination_month: z.string(),
  confirmed_quantity: decimal,
  note: text,
  created_at: z.string(),
});

export type PurchaseOrder = z.infer<typeof purchaseOrder>;
export type PurchaseDetail = z.infer<typeof purchaseDetail>;
export type Allocation = z.infer<typeof allocation>;
export type Forecast = z.infer<typeof forecast>;
export type ForecastDetail = z.infer<typeof forecastDetail>;
export type SupplierReply = z.infer<typeof reply>;

function reader<T, D>(
  path: string,
  row: z.ZodType<T>,
  detail: z.ZodType<D>,
  allowed: readonly string[]
) {
  return {
    list: async (params: URLSearchParams, signal: AbortSignal) => {
      const query = new URLSearchParams();
      for (const key of ['page', 'pageSize', ...allowed]) {
        const value = params.get(key);
        if (value) query.set(key, value);
      }
      return z
        .object({ data: z.array(row), total: z.number() })
        .parse(await apiRequest(`/procurement/${path}?${query}`, 'GET', undefined, signal));
    },
    detail: async (id: string, signal: AbortSignal) =>
      detail.parse(
        await apiRequest(`/procurement/${path}/${encodeURIComponent(id)}`, 'GET', undefined, signal)
      ),
  };
}
export const purchaseOrders = reader('purchase-orders', purchaseOrder, purchaseDetail, [
  'supplier_id',
  'status',
]);
const applicability = z.object({
  ordered_quantity: z.enum(['applicable', 'not_applicable']),
  confirmed_quantity: z.enum(['applicable', 'not_applicable']),
});
export type QuantityApplicability = z.infer<typeof applicability>;
export const allocations = {
  ...reader('allocations', allocation, allocation, []),
  list: async (params: URLSearchParams, signal: AbortSignal) => {
    const query = new URLSearchParams();
    for (const key of ['page', 'pageSize', 'purchase_order_line_id', 'order_number', 'line_number', 'status']) {
      const value = params.get(key);
      if (value) query.set(key, value);
    }
    return z.object({ data: z.array(allocation), total: z.number(), quantity_applicability: applicability.nullable().optional() })
      .parse(await apiRequest(`/procurement/allocations?${query}`, 'GET', undefined, signal));
  },
};
export const forecasts = reader('forecast-submissions', forecast, forecastDetail, []);
export const supplierReplies = reader('supplier-replies', reply, reply, [
  'product_id',
  'destination_location_id',
  'destination_month',
  'current_only',
]);

const createLineSchema = z
  .object({
    product_id: z.string().uuid(),
    location_id: z.string().uuid(),
    line_type: z.enum(['standard', 'price_adjustment']),
    quantity: z.number().positive().optional(),
    incoterms_location: z.string().trim().min(1).max(200).optional(),
    currency_id: z.string().uuid().optional(),
    adjustment_amount: z
      .string()
      .regex(/^-?\d+(\.\d+)?$/)
      .optional(),
  })
  .strict();
const createOrderSchema = z
  .object({
    supplier_id: z.string().uuid(),
    factory_id: z.string().uuid(),
    supply_route_id: z.string().uuid(),
    customer_id: z.string().uuid().optional(),
    numbering_customer_id: z.string().uuid().optional(),
    order_date: z.string().optional(),
    lines: z.array(createLineSchema).min(1),
  })
  .strict()
  .superRefine((value, context) => {
    if (!value.customer_id && !value.numbering_customer_id) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['customer_id'],
        message: 'Select a customer on the purchase order header before creating it.',
      });
    }
    if (
      value.customer_id &&
      value.numbering_customer_id &&
      value.customer_id !== value.numbering_customer_id
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['customer_id'],
        message: 'The purchase order header received conflicting customer values.',
      });
    }
  });
export type CreateOrderInput = z.infer<typeof createOrderSchema>;
const eligibleProduct = z.object({
  id: z.string(),
  product_code: z.string(),
  product_name: z.string(),
  is_active: z.boolean(),
});
const eligiblePage = z.object({
  data: z.array(eligibleProduct),
  total: z.number(),
  page: z.number(),
  pageSize: z.number(),
});
export type EligibleProduct = z.infer<typeof eligibleProduct>;
export async function listEligibleProducts(customerId: string, signal?: AbortSignal): Promise<EligibleProduct[]> {
  const rows: EligibleProduct[] = [];
  for (let page = 1; ; page += 1) {
    const params = new URLSearchParams({
      customer_id: customerId,
      page: String(page),
      pageSize: '200',
    });
    const body = eligiblePage.parse(
      await apiRequest(`/procurement/purchase-orders/eligible-products?${params}`, 'GET', undefined, signal),
    );
    rows.push(...body.data);
    if (rows.length >= body.total || body.data.length === 0) return rows;
  }
}
export async function createPurchaseOrder(input: CreateOrderInput) {
  return purchaseDetail.parse(
    await apiRequest('/procurement/purchase-orders', 'POST', createOrderSchema.parse(input))
  );
}
export async function purchaseOrderAction(id: string, action: 'review' | 'approve') {
  await apiRequest(`/procurement/purchase-orders/${encodeURIComponent(id)}/${action}`, 'POST', {});
}
const exportSchema = z.object({ id: z.string(), version: z.number(), created_at: z.string() });
export type PurchaseExport = z.infer<typeof exportSchema>;
export async function exportPurchaseOrder(id: string) {
  return exportSchema.parse(
    await apiRequest(`/procurement/purchase-orders/${encodeURIComponent(id)}/export`, 'POST', {})
  );
}
const createAllocationSchema = z
  .object({
    purchase_order_line_id: z.string().uuid(),
    order_number: z.string().regex(/^\d{10}$/),
    line_number: z.number().int().positive(),
    allocated_quantity: z.number().positive(),
    batch_code: z.string().min(1).optional(),
  })
  .strict();
export async function createAllocation(input: z.infer<typeof createAllocationSchema>) {
  return z
    .object({ allocation: allocationMutation, overAllocationWarning: z.string().nullable() })
    .parse(
      await apiRequest('/procurement/allocations', 'POST', createAllocationSchema.parse(input))
    );
}

export async function cancelAllocation(id: string, reason: string) {
  const input = z
    .object({ cancellation_reason: z.string().trim().min(1) })
    .parse({ cancellation_reason: reason });
  await apiRequest(`/procurement/allocations/${encodeURIComponent(id)}/cancel`, 'POST', input);
}

const redistributeAllocationSchema = z
  .object({
    purchase_order_line_id: z.string().uuid(),
    source_allocation_id: z.string().uuid(),
    destination_allocation_id: z.string().uuid().optional(),
    order_number: z.string().regex(/^\d{10}$/).optional(),
    line_number: z.number().int().positive().optional(),
    batch_code: z.string().trim().min(1).optional(),
    quantity: z.number().positive(),
    reason: z.string().trim().min(1),
  })
  .strict()
  .superRefine((value, ctx) => {
    const namedDestination =
      value.order_number !== undefined ||
      value.line_number !== undefined ||
      value.batch_code !== undefined;
    if (value.destination_allocation_id) {
      if (namedDestination) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['destination_allocation_id'],
          message: 'Provide either a destination allocation or a customer order line, not both',
        });
      }
      return;
    }
    if (!value.order_number || value.line_number === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['destination_allocation_id'],
        message: 'Provide a destination allocation or a customer order line',
      });
    }
  });
export async function redistributeAllocation(input: z.infer<typeof redistributeAllocationSchema>) {
  return z
    .object({
      source: allocationMutation,
      destination: allocationMutation,
      quantity: z.union([z.string(), z.number()]),
      overAllocationWarning: z.string().nullable(),
    })
    .parse(
      await apiRequest(
        '/procurement/allocations/redistribute',
        'POST',
        redistributeAllocationSchema.parse(input)
      )
    );
}

export async function allocationCoverage(po: PurchaseDetail, signal: AbortSignal) {
  return Promise.all(
    po.lines
      .filter((line) => line.line_type === 'standard')
      .map(async (line) => {
        const counts = await Promise.all(
          ['planned', 'confirmed'].map((status) =>
            allocations.list(
              new URLSearchParams({
                purchase_order_line_id: line.id,
                status,
                page: '1',
                pageSize: '1',
              }),
              signal
            )
          )
        );
        return { ...line, covered: counts.some((result) => result.total > 0) };
      })
  );
}

/** Compatibility lookup until a PO-line detail API exists. Used only for a line-only deep link. */
export async function purchaseOrderForLine(lineId: string, signal: AbortSignal) {
  const linked = await allocations.list(
    new URLSearchParams({ purchase_order_line_id: lineId, page: '1', pageSize: '1' }),
    signal
  );
  const parent = linked.data[0]?.purchase_order_id;
  if (parent) return purchaseOrders.detail(parent, signal);
  for (let page = 1, seen = 0; ; page++) {
    const pos = await purchaseOrders.list(
      new URLSearchParams({ page: String(page), pageSize: '100' }),
      signal
    );
    for (const po of pos.data) {
      const detail = await purchaseOrders.detail(po.id, signal);
      if (detail.lines.some((line) => line.id === lineId)) return detail;
    }
    seen += pos.data.length;
    if (!pos.data.length || seen >= pos.total)
      throw new Error('The purchase order for this line could not be found.');
  }
}

const forecastInput = z
  .object({
    submission_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    lines: z
      .array(
        z
          .object({
            product_id: z.string().uuid(),
            destination_location_id: z.string().uuid(),
            fab_phase_id: z.string().uuid(),
            planned_shipping_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
            quantity: z.number().finite().nonnegative(),
          })
          .strict()
      )
      .min(1),
  })
  .strict();
export type ForecastInput = z.infer<typeof forecastInput>;
export async function createForecast(input: ForecastInput) {
  return forecastDetail.parse(
    await apiRequest('/procurement/forecast-submissions', 'POST', forecastInput.parse(input))
  );
}
export async function exportForecast(id: string, supplierCode?: string) {
  return exportSchema.parse(
    await apiRequest(
      `/procurement/forecast-submissions/${encodeURIComponent(id)}/export`,
      'POST',
      supplierCode ? { supplier_code: supplierCode } : {}
    )
  );
}

export async function updateIncotermsLocation(id: string, value: string | null) {
  const location = z.string().trim().min(1).max(200).nullable().parse(value);
  await apiRequest(`/procurement/purchase-order-lines/${encodeURIComponent(id)}/incoterms-location`, 'PATCH', { incoterms_location: location });
}
